import React, { useMemo, useState, useEffect } from 'react';
import {
	invoiceDetailAttributes,
	billingInformationFields,
	mobileAssetColumns,
} from '../../../components/DataGrid/columnDefinitions';
import { useSelector, useDispatch } from 'react-redux';
import { selectActiveFilters } from '../../../redux/slices/filterSlice';
import { Box, Typography, useMediaQuery, useTheme, Alert } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { useLazyGetAssetsByInvoiceNumberQuery } from '../../../api/asset.api';
import type { InvoiceAsset } from '../../../api/models/invoiceAsset.model';
import { useGetInvoiceQuery } from '../../../api/invoice.api';
import DataGridProComponent from '../../../components/DataGrid/DataGridProComponent';
import { type RootState } from '../../../redux/store';
import { InvoiceRelatedAssetsDataGridType } from '../../../components/DataGrid/DataGridTypes';
import type { GridDataSource } from '@mui/x-data-grid-pro';
import { showAlert } from '../../../redux/slices/alertSlice';
import Assets from '../../Contracts/Assets/Assets';
import InvoiceDetailsSkeleton from './InvoiceDetailsSkeleton';
import { INVOICE_CTA, UNABLE_TO_LOAD_INVOICE } from '../../../constants/messages';
import { INVOICE_ASSET_TYPE } from '../../../constants/logic';

/**
 * Component to display detailed invoice information including
 * invoice details, billing information, and related assets.
 * Gets the invoice number from Redux store.
 */
const InvoiceDetails: React.FC = () => {
	const dispatch = useDispatch();
	const selectedInvoiceNumber = useSelector(
		(state: RootState) => state.invoices.selectedInvoiceDetailsViewId
	);

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	// Fetch detailed invoice data using invoice number from store
	const {
		data: invoiceDetails,
		isLoading,
		error,
	} = useGetInvoiceQuery(selectedInvoiceNumber!, {
		skip: !selectedInvoiceNumber,
	});

	const [dataIsLoading, setDataIsLoading] = useState(false);
	const [invoiceAssets, setInvoiceAssets] = useState<InvoiceAsset[]>([]);

	// Get filters from Redux state
	const assetFilters = useSelector(selectActiveFilters);

	const [getInvoiceAssets] = useLazyGetAssetsByInvoiceNumberQuery();

	// Fetch assets by invoice number (paginated), now supporting filters
	const assetDataSource: GridDataSource = useMemo(
		() => ({
			getRows: async ({ paginationModel, sortModel }) => {
				if (!selectedInvoiceNumber) {
					return { rows: [], rowCount: 0 };
				}
				setDataIsLoading(true);
				const result = await getInvoiceAssets(
					{
						invoiceNumber: selectedInvoiceNumber,
						page: paginationModel?.page ?? 0,
						pageSize: paginationModel?.pageSize ?? 50,
						sortDirection: sortModel?.[0]?.sort || '',
						sortField: sortModel?.[0]?.field || '',
						filters: assetFilters,
					},
					true
				).unwrap();
				setInvoiceAssets(result?.content ?? []);
				setDataIsLoading(false);
				return {
					rows: result?.content ?? [],
					rowCount: result?.totalElements ?? 0,
				};
			},
		}),
		[selectedInvoiceNumber, assetFilters, getInvoiceAssets]
	);

	// Memoize the note component to prevent recreation on each render
	const invoiceNote = useMemo(
		() => (
			<Alert
				icon={<InfoIcon />}
				severity="info"
				sx={{
					mt: 2,
					backgroundColor: theme.palette.background.paper,
					border: `1px solid ${theme.palette.divider}`,
				}}
			>
				<Box sx={{ display: 'flex', flexDirection: 'column' }}>
					<strong>Note:</strong>
					<Box mt={0.5} fontSize="1rem">
						{INVOICE_CTA}
					</Box>
				</Box>
			</Alert>
		),
		[theme.palette.background.paper, theme.palette.divider]
	);

	// Show error alert if fetch fails
	useEffect(() => {
		if (error) {
			dispatch(
				showAlert({
					message: 'Failed to load invoice details. Please try again.',
					type: 'error',
				})
			);
		}
	}, [error, dispatch]);

	// Show skeleton while loading
	if (isLoading) {
		return <InvoiceDetailsSkeleton />;
	}

	// If no data after loading, show error state
	if (!invoiceDetails) {
		return (
			<Box sx={{ p: 3 }}>
				<Typography variant="h6" color="error">
					{UNABLE_TO_LOAD_INVOICE}
				</Typography>
			</Box>
		);
	}

	// Helper to render field values with special handling for contractNumbers
	const renderFieldValue = (field: any) => {
		const value = invoiceDetails[field.field as keyof typeof invoiceDetails];

		// Apply formatter if provided
		if (field.valueFormatter && value !== null && value !== undefined) {
			return field.valueFormatter(value);
		}

		// Return value or dash
		return value ?? '-';
	};

	const mobileView = () => (
		<Box
			display="flex"
			flexDirection="column"
			gap={1}
			mt={1}
			data-testid="invoice-details-mobile-view"
		>
			<h3>Invoice Details</h3>
			<Box>
				{invoiceDetailAttributes.map((field, index) => (
					<Box key={index} display="flex" flexDirection="column">
						<strong style={{ fontSize: '1.1rem' }} data-testid={`invoice-detail-${field.field}`}>
							{field.headerName}:
						</strong>
						<Box mt={0.5} fontSize="1rem" data-testid={`${field.field}-value`}>
							{renderFieldValue(field)}
						</Box>
					</Box>
				))}
			</Box>

			<h3>Billing Information</h3>
			<Box>
				{billingInformationFields.map((field, index) => (
					<Box key={index} display="flex" flexDirection="column">
						<strong style={{ fontSize: '1.1rem' }} data-testid={`invoice-detail-${field.field}`}>
							{field.headerName}:
						</strong>
						<Box mt={0.5} fontSize="1rem" data-testid={`${field.field}-value`}>
							{renderFieldValue(field)}
						</Box>
					</Box>
				))}
			</Box>

			<h3>Related Assets</h3>
			<Box mt={2}>
				<DataGridProComponent
					columns={mobileAssetColumns}
					dataGridType={InvoiceRelatedAssetsDataGridType}
					checkboxEnabled={true}
					dataIsLoading={dataIsLoading}
				/>
			</Box>
		</Box>
	);

	const desktopView = () => (
		<Box>
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
				<Typography
					variant="h6"
					component="h4"
					sx={{ color: (theme) => theme.palette.primary.main, fontWeight: 'bold' }}
				>
					Invoice Details
				</Typography>
			</Box>
			<Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={2} mt={2}>
				{invoiceDetailAttributes.map((field, index) => (
					<Box key={index} display="flex" flexDirection="column">
						<strong style={{ fontSize: '1.1rem' }} data-testid={`invoice-detail-${field.field}`}>
							{field.headerName}:
						</strong>
						<Box mt={0.5} fontSize="1rem" data-testid={`${field.field}-value`}>
							{renderFieldValue(field)}
						</Box>
					</Box>
				))}
			</Box>

			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 4 }}>
				<Typography
					variant="h6"
					component="h4"
					sx={{ color: (theme) => theme.palette.primary.main, fontWeight: 'bold' }}
				>
					Billing Information
				</Typography>
			</Box>
			<Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={2} mt={2}>
				{billingInformationFields.map((field, index) => (
					<Box key={index} display="flex" flexDirection="column">
						<strong style={{ fontSize: '1.1rem' }} data-testid={`invoice-detail-${field.field}`}>
							{field.headerName}:
						</strong>
						<Box mt={0.5} fontSize="1rem" data-testid={`${field.field}-value`}>
							{renderFieldValue(field)}
						</Box>
					</Box>
				))}
			</Box>

			<Box mt={2}>
				<Assets
					assetsDataSource={assetDataSource}
					assetData={invoiceAssets}
					dataIsLoading={dataIsLoading}
					assetType={INVOICE_ASSET_TYPE}
				/>
			</Box>
		</Box>
	);

	return (
		<>
			<Box
				sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}
				data-testid="invoice-details-header"
			>
				<Typography variant="h3" component="h3" sx={{ fontWeight: 'bold' }}>
					Invoice No. {invoiceDetails.invoiceNumber}
				</Typography>
			</Box>

			{isMobile ? mobileView() : desktopView()}
			{invoiceNote}
		</>
	);
};

export default InvoiceDetails;
